import {
  JOURNEYS_HISTORY_TABLE,
  ROI_ANALYTICS_TABLE,
  SKAN_UNIFIED_TABLE,
  TABLE_TO_TOTALS_MAPPING,
} from '../../../../../shared/constants/analytics/contexts';
import type { ReportQuery } from 'client/js/main/types/analytics';
import type { ReportActiveMode } from '../../../../../shared/types/roi-hub/TableModeSwitcher.types';
import { SummaryProductContextService } from '../../../../../shared/SummaryProductContext.service';
import {
  ANALYTICS_ENDPOINT_BY_CONTEXT,
  ANALYTICS_REPORT_TOTALS_ENDPOINT_BY_CONTEXT,
} from '../../constants/analytics/endpoints';
import { SummaryEndpointsService } from '../../screens/ScreenNewSummary/SummaryEndpoints.service';

type ContextQuery = { context: string };

export class DomainAnalyticsService {
  static build(): DomainAnalyticsService {
    return new DomainAnalyticsService();
  }

  summaryEndpointsService: SummaryEndpointsService;

  drilldownEnabledContexts: Set<string>;

  constructor() {
    this.summaryEndpointsService = new SummaryEndpointsService();
    this.drilldownEnabledContexts = new Set([ROI_ANALYTICS_TABLE, JOURNEYS_HISTORY_TABLE, SKAN_UNIFIED_TABLE]);
  }

  getAnalyticsEndpoint({ context }: ContextQuery): string | undefined {
    if (SummaryProductContextService.isSummaryProductContext(context)) {
      return this.summaryEndpointsService.getSummaryEndpointByContext(context);
    }

    return ANALYTICS_ENDPOINT_BY_CONTEXT[context];
  }

  getTotalsContext({ context }: ContextQuery): string | undefined {
    const isSummaryProductContext = SummaryProductContextService.isSummaryProductContext(context);

    if (isSummaryProductContext) {
      const [baseContext, product] = SummaryProductContextService.decodeContext(context);
      return SummaryProductContextService.encodeContext(TABLE_TO_TOTALS_MAPPING[baseContext], product);
    } else {
      return TABLE_TO_TOTALS_MAPPING[context] || context;
    }
  }

  getTotalsEndpoint({ context }: ContextQuery): string | undefined {
    if (SummaryProductContextService.isSummaryProductContext(context)) {
      return this.summaryEndpointsService.getTotalsEndpointByContext(context);
    }

    return ANALYTICS_REPORT_TOTALS_ENDPOINT_BY_CONTEXT[context];
  }

  isDomainAnalyticsAvailable({ context }: ContextQuery): boolean {
    if (SummaryProductContextService.isSummaryProductContext(context)) {
      const [baseContext] = SummaryProductContextService.decodeContext(context);
      return !!ANALYTICS_ENDPOINT_BY_CONTEXT[baseContext];
    }

    return !!ANALYTICS_ENDPOINT_BY_CONTEXT[context];
  }

  isDrillDownEnabled(context?: string): boolean {
    return !!context && this.drilldownEnabledContexts.has(context);
  }

  isTreeView(reportQuery: ReportQuery, activeMode?: ReportActiveMode) {
    return activeMode === 'tree' && this.isDrillDownEnabled(reportQuery.context);
  }

  adjustQueryForTreeView(reportQuery: ReportQuery) {
    const [topLevelDimension] = reportQuery.dimensions;
    reportQuery.dimensionsToExplain = reportQuery.dimensions;
    reportQuery.dimensions = [topLevelDimension];
    return reportQuery;
  }
}
